import moment from 'moment'
import React from 'react'
import { TidewaysContext } from '../app/context'
import Navigator from '../app/navigator'
import { ActiveSlot } from '../domain/ActiveSlot'
import { GraphEntity } from '../domain/GraphEntity'
import Chart from '../monitor/graph.d3'
import Downstream from '../monitor/graph/downstream'
import Snapshot from '../monitor/snapshot/snapshotModel'
import { newResizeObserver } from '../helper/resizeObserver'
import { convertDate } from '../helper/convertDate'

interface Props {
    tidewaysContext: TidewaysContext
    navigator: Navigator
    parameters: { [key: string]: string }
    loading: boolean
    data: GraphEntity
    moveUpTooltip?: number
    dataFixedTimezone: string
    changeActiveSlot: (activeSlot: ActiveSlot) => void
}

interface State {
    activeSlot: ActiveSlot
}

export class GraphComponent extends React.PureComponent<Props, State> {
    public static displayName = 'Graph'

    public static defaultProps = {
        loading: false,
        data: null,
    }

    public state: State = {
        activeSlot: {
            timeIndex: null,
            mouse: null,
        },
    }

    private nodes: { svg: SVGSVGElement | null; chart: HTMLDivElement | null } = {
        svg: null,
        chart: null,
    }

    private chartDownstream
    private chart
    private resizeObserver

    private destroyChart = () => {
        this.chart?.destroy() // remove svg-elements from dom
        this.chart = null // remove reference to the object
    }

    private rebuildChart = () => {
        this.destroyChart()
        this.createChart()
        if (this.chart) this.updateChart()
    }

    private setResizeObserver = () => {
        const chartElement = this.getChartElement()
        if (chartElement) {
            const parentNode = jQuery(chartElement).parent()
            this.resizeObserver = newResizeObserver(this.rebuildChart)
            this.resizeObserver.observe(parentNode[0])
        }
    }

    public createChart() {
        this.chart = new Chart()

        if (this.nodes.chart) {
            jQuery(this.nodes.chart).on('eventClicked', (event, group) => {
                if (typeof group === 'undefined') {
                    return
                }

                this.props.navigator.push(
                    {
                        event: group.id,
                        events: group.id,
                    },
                    'XhprofOrganizationBundle.Application.show',
                )
            })
        }

        const chartElement = this.getChartElement()
        if (chartElement) {
            const parentNode = jQuery(chartElement).parent()

            this.chart.create(chartElement, parentNode.width(), 180, (timeIndex, timeIndexNext, mouse) =>
                this.showTooltip(timeIndex, timeIndexNext, mouse, 'main'),
            )
        }
        if (this.props.data) {
            this.updateChart()
        }
    }

    public componentDidMount() {
        if (!this.chart) {
            this.createChart()
        }
    }

    public showTooltip(timestamp, timestampNext, mouse, source) {
        if (source === 'downstream' && this.chart) {
            this.chart.highlight(
                convertDate(timestamp, this.props.dataFixedTimezone),
                convertDate(timestampNext, this.props.dataFixedTimezone),
            )
        }

        if (source === 'main' && this.chartDownstream) {
            this.chartDownstream.highlight(
                convertDate(timestamp, this.props.dataFixedTimezone),
                convertDate(timestampNext, this.props.dataFixedTimezone),
            )
        }

        this.props.changeActiveSlot({
            timeIndex: timestamp,
            mouse,
        })
    }

    public componentDidUpdate(prevProps) {
        if (!this.chart) {
            this.createChart()
        }

        if (!this.resizeObserver && this.chart) {
            this.setResizeObserver()
        }

        if (this.props.data) {
            this.updateChart()
        }
    }

    public componentWillUnmount() {
        this.destroyChart()
        this.resizeObserver?.disconnect()
    }

    public getChartElement = () => {
        return this.nodes.svg
    }

    public updateChart = () => {
        this.chart.update(
            [
                {
                    name: 'requests',
                    data: Object.entries(this.props.data.report.requests).map(([key, value]) => {
                        return {
                            axis: 'requests',
                            date: convertDate(key, this.props.dataFixedTimezone),
                            dateUtc: key,
                            value,
                            responseTime: this.props.data.report.percentileResponseTimes[key] || null,
                            requests: this.props.data.report.requests[key] || null,
                            errors: this.props.data.report.errors[key] || null,
                        }
                    }),
                },
                {
                    name: 'response',
                    data: Object.entries(this.props.data.report.percentileResponseTimes).map(([key, value]) => {
                        return {
                            axis: 'response',
                            date: convertDate(key, this.props.dataFixedTimezone),
                            dateUtc: key,
                            value,
                        }
                    }),
                },
                {
                    name: 'errors',
                    data: Object.entries(this.props.data.report.errors).map(([key, value]) => {
                        return {
                            axis: 'requests',
                            date: convertDate(key, this.props.dataFixedTimezone),
                            value,
                        }
                    }),
                },
            ],
            this.props.data.events.map(event =>
                moment.isDate(event.eventDate)
                    ? event
                    : {
                          ...event,
                          eventDate: convertDate(event.eventDate, this.props.dataFixedTimezone),
                      },
            ),
            this.props.parameters.end ? Snapshot.fromParameters(this.props.parameters).getRangeMarker() : [],
            this.props.data.report.hasDownstream || false,
        )
    }

    public render() {
        let hasDownstream = false
        if (this.props.data && this.props.data.report) {
            hasDownstream = this.props.data.report.hasDownstream
        }

        return (
            <div
                ref={element => {
                    this.nodes.chart = element
                }}
            >
                <div className="chart">
                    <svg
                        ref={element => {
                            this.nodes.svg = element
                        }}
                    />
                </div>
                {hasDownstream ? (
                    <Downstream
                        {...this.props}
                        onTimestampSelected={(timestamp, timestampNext, mouse) =>
                            this.showTooltip(timestamp, timestampNext, mouse, 'downstream')
                        }
                        onChartCreated={chart => {
                            this.chartDownstream = chart
                        }}
                        dataFixedTimezone={this.props.dataFixedTimezone}
                    />
                ) : null}
            </div>
        )
    }
}
